import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { ZNSAddressDisplay } from '../../components/shared/ZNSAddressDisplay';
import { useRelayPrice } from '../../hooks/useRelayPrice';
import { useTokenChartData, CandleData } from '../../hooks/useTokenChartData';
import { formatMarketCap } from '../../utils/address';
import { getEthUsdPrice } from '../../utils/ethPrice';
import { getTokenInfo, getPoolLiquidityUSD, type TokenInfo } from '../../utils/blockscoutApi';
import CustomSwapWidget from '../../components/swap/CustomSwapWidget';
import TokenComments from '../../components/token/TokenComments';
import TradingPanel from '../../components/token/TradingPanel';
import { Copy, ExternalLink, ArrowUpRight, ArrowDownRight, Globe, Send, Twitter, Activity, Droplets, Users, Clock, Shield, X } from 'lucide-react';
import { sanitizeUrl } from '../../utils/sanitizeUrl';

const GECKOTERMINAL_API = 'https://api.geckoterminal.com/api/v2';
const INK_NETWORK = 'ink';

// PWA Container with bottom padding for nav
const PWAContainer = styled.div`
  min-height: calc(100vh - 80px);
  width: 100%;
  background: #000000;
  color: #e4e4e7;
  font-family: 'Plus Jakarta Sans', monospace;
  display: flex;
  flex-direction: column;
  padding-bottom: 100px;
`;

const CoverBanner = styled.div<{ $imageUrl?: string }>`
  position: relative;
  height: 192px;
  width: 100%;
  overflow: hidden;
  border-bottom: 1px solid #333333;
  
  ${props => props.$imageUrl ? `
    background-image: url(${props.$imageUrl});
    background-size: cover;
    background-position: center;
  ` : `
    background: linear-gradient(135deg, #000000 0%, #000000 100%);
  `}
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.9));
  }
`;

const BannerContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    background: linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.8));
  }
`;

const BannerLogo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid #333333;
  background: #000000;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.8);
`;

const BannerInfo = styled.div`
  flex: 1;
  padding-bottom: 8px;
`;

const BannerTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerSubtitle = styled.p`
  font-size: 14px;
  color: #d4d4d8;
  margin: 4px 0 0 0;
`;

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
`;

const SocialButton = styled.a`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #444444;
  background: rgba(9, 9, 11, 0.8);
  backdrop-filter: blur(8px);
  color: #d4d4d8;
  transition: all 0.2s;
  text-decoration: none;
  
  &:hover {
    border-color: #444444;
    background: #000000;
    color: #ffffff;
  }
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid #333333;
  background: rgba(9, 9, 11, 0.9);
  backdrop-filter: blur(12px);
`;

const TopBarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const TokenInfoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TokenLogoSmall = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #333333;
`;

const TokenPair = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TokenSymbolBar = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #e4e4e7;
`;

const TokenPairLabel = styled.span`
  font-size: 12px;
  color: #71717a;
`;

const Divider = styled.div`
  width: 1px;
  height: 16px;
  background: #333333;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PriceValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
`;

const PriceChange = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
`;

const PriceEth = styled.div`
  font-size: 10px;
  color: #444444;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatLabel = styled.span`
  color: #71717a;
`;

const StatValue = styled.span`
  font-weight: 500;
  color: #d4d4d8;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-direction: column; // Always column for PWA
  overflow-y: auto;
  height: auto;
`;

const LeftSidebar = styled.div`
  width: 100%;
  border-bottom: 1px solid #333333;
  background: rgba(9, 9, 11, 0.5);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TerminalCard = styled.div`
  border-radius: 4px;
  border: 1px solid #333333;
  background: rgba(9, 9, 11, 0.5);
  backdrop-filter: blur(4px);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #333333;
  padding: 8px 12px;
`;

const CardTitle = styled.h3`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #a1a1aa;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 12px;
`;

const PriceDisplay = styled.div`
  margin-bottom: 12px;
`;

const PriceLabel = styled.p`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #444444;
  margin: 0 0 4px 0;
`;

const PriceAmount = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
  margin: 0 0 4px 0;
`;

const PriceEthAmount = styled.p`
  font-size: 12px;
  color: #71717a;
  margin: 0;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  padding: 4px 0;
`;

const StatRowLabel = styled.span`
  color: #71717a;
`;

const StatRowValue = styled.span<{ $highlight?: boolean }>`
  color: ${props => props.$highlight ? '#10b981' : '#d4d4d8'};
  font-weight: ${props => props.$highlight ? '500' : '400'};
  font-family: ${props => props.$highlight ? 'monospace' : 'inherit'};
`;

const DividerLine = styled.div`
  height: 1px;
  background: #000000;
  margin: 12px 0;
`;

const ContractDisplay = styled.div`
  margin-bottom: 12px;
`;

const ContractAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 0;
`;

const ContractText = styled.code`
  font-size: 11px;
  color: #a1a1aa;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconButton = styled.button`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #444444;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #a1a1aa;
  }
`;

const SocialLinksRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SocialLinkSmall = styled.a`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid #333333;
  background: rgba(9, 9, 11, 0.5);
  color: #a1a1aa;
  transition: all 0.2s;
  text-decoration: none;
  
  &:hover {
    border-color: #444444;
    color: #d4d4d8;
  }
`;

const MetricCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 4px;
  border: 1px solid #333333;
  background: #000000;
  padding: 10px 12px;
`;

const MetricIcon = styled.div<{ $trend?: 'up' | 'down' }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: ${props =>
    props.$trend === 'up' ? 'rgba(16, 185, 129, 0.1)' :
      props.$trend === 'down' ? 'rgba(239, 68, 68, 0.1)' :
        '#333333'};
  color: ${props =>
    props.$trend === 'up' ? '#10b981' :
      props.$trend === 'down' ? '#ef4444' :
        '#a1a1aa'};
`;

const MetricInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MetricLabel = styled.p`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #71717a;
  margin: 0 0 2px 0;
`;

const MetricValue = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #e4e4e7;
  margin: 0;
`;

const MetricChange = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
`;

const CenterSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
  height: auto;
  min-height: 600px;
`;

const ChartSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #333333;
`;

const RightSidebar = styled.div`
  width: 100%;
  border-top: 1px solid #333333;
  background: rgba(9, 9, 11, 0.5);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AboutCard = styled(TerminalCard)`
  margin-top: 12px;
`;

const AboutText = styled.p`
  font-size: 12px;
  line-height: 1.6;
  color: #a1a1aa;
  margin: 0;
`;

const AuditBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #71717a;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #333333;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #71717a;
  font-size: 16px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 18px;
`;

const BackButton = styled.button`
  background: #000000;
  border: 1px solid #333333;
  border-radius: 8px;
  padding: 10px 20px;
  color: #a855f7;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #000000;
  }
`;

const HeaderTradeButton = styled.button`
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  border: 1px solid #a855f7;
  border-radius: 6px;
  padding: 8px 16px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: block;
  width: 100%;
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 4px 12px #333333;
  
  &:hover {
    opacity: 0.9;
    box-shadow: 0 0 15px #444444;
  }
`;

// Helper function to get pool data from GeckoTerminal
const fetchPoolData = async (poolAddress: string) => {
  try {
    const url = `${GECKOTERMINAL_API}/networks/${INK_NETWORK}/pools/${poolAddress}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch pool data');
    const data = await response.json();
    return data.data?.attributes;
  } catch (error) {
    console.error('Error fetching pool data:', error);
    return null;
  }
};

// Swap Modal Styles
const SwapModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const SwapModalContent = styled.div`
  background: #000000;
  border: 1px solid #333333;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SwapModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #333333;
`;

const SwapModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const SwapModalClose = styled.button`
  background: transparent;
  border: none;
  color: #71717a;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Helper to get pool address from token address
const getPoolAddress = async (tokenAddress: string): Promise<string | null> => {
  try {
    const url = `${GECKOTERMINAL_API}/networks/${INK_NETWORK}/tokens/${tokenAddress}/pools?page=1`;
    const response = await fetch(url);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.data && data.data.length > 0) {
      return data.data[0].id.split('_')[1];
    }
    return null;
  } catch (error) {
    console.error('Error fetching pool address:', error);
    return null;
  }
};

const TokenDetailsPagePWA: React.FC = () => {
  const { tokenAddress } = useParams<{ tokenAddress: string }>();
  const navigate = useNavigate();
  const [token, setToken] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [poolData, setPoolData] = useState<any>(null);
  const [poolAddress, setPoolAddress] = useState<string | null>(null);
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [ethPriceUSD, setEthPriceUSD] = useState<number>(0);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [blockscoutLiquidity, setBlockscoutLiquidity] = useState<number | null>(null);

  const explorerLinkPrefix = 'https://explorer.inkonchain.com';

  // Fetch ETH price for trade calculations
  useEffect(() => {
    getEthUsdPrice().then(setEthPriceUSD).catch(console.error);
  }, []);

  // Fetch token info (holders, transfers) from Blockscout
  useEffect(() => {
    if (token?.contract_address) {
      getTokenInfo(token.contract_address).then(info => {
        if (info) setTokenInfo(info);
      }).catch(console.error);
    }
  }, [token?.contract_address]);

  // Fetch pool liquidity from Blockscout
  useEffect(() => {
    if (poolAddress && ethPriceUSD > 0) {
      getPoolLiquidityUSD(poolAddress, ethPriceUSD).then(result => {
        if (result) setBlockscoutLiquidity(result.liquidityUSD);
      }).catch(console.error);
    }
  }, [poolAddress, ethPriceUSD]);

  // Get price data for this token
  const { getPriceForToken } = useRelayPrice(token ? [token] : []);
  const priceUSD = token?.contract_address ? getPriceForToken(token.contract_address) : null;

  // Get chart data for calculations (24h change, volume, etc.)
  const { data: chartData } = useTokenChartData(token?.contract_address || null, '1D');

  // Helper function to safely parse and validate numbers
  const getValidNumber = (val: any): number | null => {
    if (val === null || val === undefined) return null;
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num) || !isFinite(num)) return null;
    return num;
  };

  // Fetch token data and pool info
  useEffect(() => {
    const fetchData = async () => {
      if (!tokenAddress) {
        setError('No token address provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch token from Supabase
        const { data, error: fetchError } = await supabase
          .from('tokens')
          .select('*')
          .eq('contract_address', tokenAddress)
          .single();

        if (fetchError) {
          setError('Token not found');
          setLoading(false);
          return;
        }

        setToken(data);

        // Try to get pool address (use token's pool_address if available, otherwise fetch from GeckoTerminal)
        let poolAddr = data.pool_address;
        if (!poolAddr) {
          poolAddr = await getPoolAddress(tokenAddress);
        }
        setPoolAddress(poolAddr);

        // Fetch pool data if we have a pool address
        if (poolAddr) {
          const poolInfo = await fetchPoolData(poolAddr);
          setPoolData(poolInfo);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load token');
        setLoading(false);
      }
    };

    fetchData();
  }, [tokenAddress]);

  // Calculate 24h change from chart data or pool data
  const priceChange24h = chartData && chartData.length >= 2
    ? (() => {
      const firstClose = getValidNumber(chartData[0].close);
      const lastClose = getValidNumber(chartData[chartData.length - 1].close);
      if (firstClose === null || lastClose === null || firstClose === 0) return null;
      const change = ((lastClose - firstClose) / firstClose) * 100;
      return isNaN(change) || !isFinite(change) ? null : change;
    })()
    : getValidNumber(poolData?.price_change_percentage?.h24);

  // Get 24h volume from pool data (more accurate) or calculate from chart data
  const volume24h = poolData?.volume_usd?.h24
    ? (typeof poolData.volume_usd.h24 === 'number' ? poolData.volume_usd.h24 : parseFloat(poolData.volume_usd.h24) || 0)
    : (chartData && chartData.length > 0 ? chartData.reduce((sum: number, candle: CandleData) => sum + (Number(candle.volume) || 0), 0) : 0);

  // Get high/low from chart data or pool data
  const high24h = chartData && chartData.length > 0
    ? (() => {
      const highs = chartData.map((c: CandleData) => getValidNumber(c.high)).filter((v): v is number => v !== null);
      return highs.length > 0 ? Math.max(...highs) : null;
    })()
    : getValidNumber(poolData?.high_token_1_price);

  const low24h = chartData && chartData.length > 0
    ? (() => {
      const lows = chartData.map((c: CandleData) => getValidNumber(c.low)).filter((v): v is number => v !== null);
      return lows.length > 0 ? Math.min(...lows) : null;
    })()
    : getValidNumber(poolData?.low_token_1_price);

  if (loading) {
    return (
      <PWAContainer>
        <LoadingState>Loading token details...</LoadingState>
      </PWAContainer>
    );
  }

  if (error || !token) {
    return (
      <PWAContainer>
        <ErrorState>
          <ErrorMessage>{error || 'Token not found'}</ErrorMessage>
          <BackButton onClick={() => navigate('/pwa/explore')}>
            ← Back to Explore
          </BackButton>
        </ErrorState>
      </PWAContainer>
    );
  }

  return (
    <PWAContainer>
      {/* Cover Banner */}
      <CoverBanner $imageUrl={token.cover_photo_url}>
        <BannerContent>
          {token.logo_url && <BannerLogo src={token.logo_url} alt={token.name} />}
          <BannerInfo>
            <BannerTitle>
              {token.name}
              <span style={{ fontSize: '14px', color: '#71717a', fontWeight: '400' }}>Ink</span>
            </BannerTitle>
            {token.description && (
              <BannerSubtitle>{token.description.substring(0, 100)}</BannerSubtitle>
            )}
          </BannerInfo>
          <BannerActions>
            {sanitizeUrl(token.website) && (
              <SocialButton href={sanitizeUrl(token.website)!} target="_blank" rel="noopener noreferrer">
                <Globe size={16} />
              </SocialButton>
            )}
            {sanitizeUrl(token.twitter) && (
              <SocialButton href={sanitizeUrl(token.twitter)!} target="_blank" rel="noopener noreferrer">
                <Twitter size={16} />
              </SocialButton>
            )}
            {sanitizeUrl(token.telegram) && (
              <SocialButton href={sanitizeUrl(token.telegram)!} target="_blank" rel="noopener noreferrer">
                <Send size={16} />
              </SocialButton>
            )}
          </BannerActions>
        </BannerContent>
      </CoverBanner>

      {/* Top Bar */}
      <TopBar>
        <TopBarContent>
          <TopBarLeft>
            <TokenInfoBar>
              {token.logo_url && <TokenLogoSmall src={token.logo_url} alt={token.symbol} />}
              <TokenPair>
                <TokenSymbolBar>{token.symbol}</TokenSymbolBar>
                <TokenPairLabel>/ETH</TokenPairLabel>
              </TokenPair>
            </TokenInfoBar>
            <Divider />
            <PriceInfo>
              <PriceValue>
                {priceUSD && typeof priceUSD === 'number' && !isNaN(priceUSD) && isFinite(priceUSD) ? `$${priceUSD < 0.01 ? priceUSD.toFixed(6) : priceUSD.toFixed(4)}` : 'Loading...'}
              </PriceValue>
              {priceChange24h !== null && typeof priceChange24h === 'number' && !isNaN(priceChange24h) && isFinite(priceChange24h) && (
                <PriceChange $positive={priceChange24h >= 0}>
                  {priceChange24h >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {Math.abs(priceChange24h).toFixed(2)}%
                </PriceChange>
              )}
              <PriceEth>≈ {priceUSD && typeof priceUSD === 'number' && !isNaN(priceUSD) && isFinite(priceUSD) ? (priceUSD / 3000).toFixed(8) : '0.00000000'} ETH</PriceEth>
            </PriceInfo>
          </TopBarLeft>
          <TopBarRight>
            <StatsBar>
              <StatItem>
                <StatLabel>24h Vol:</StatLabel>
                <StatValue>${volume24h && typeof volume24h === 'number' && !isNaN(volume24h) && isFinite(volume24h) ? (volume24h >= 1000000 ? (volume24h / 1000000).toFixed(2) + 'M' : volume24h >= 1000 ? (volume24h / 1000).toFixed(2) + 'K' : volume24h.toFixed(2)) : '0'}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>MCap:</StatLabel>
                <StatValue>{priceUSD ? formatMarketCap(priceUSD) : 'Loading...'}</StatValue>
              </StatItem>
            </StatsBar>
          </TopBarRight>
          <HeaderTradeButton onClick={() => setSwapModalOpen(true)}>
            Trade {token?.symbol}
          </HeaderTradeButton>
        </TopBarContent>
      </TopBar>

      {/* Main Content */}
      <MainContent>
        {/* Market Stats */}
        <LeftSidebar>
          <TerminalCard>
            <CardHeader>
              <CardTitle>Market Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <PriceDisplay>
                <PriceLabel>Price</PriceLabel>
                <PriceAmount>
                  {priceUSD && typeof priceUSD === 'number' && !isNaN(priceUSD) && isFinite(priceUSD) ? `$${priceUSD < 0.01 ? priceUSD.toFixed(6) : priceUSD.toFixed(4)}` : 'Loading...'}
                </PriceAmount>
                <PriceEthAmount>
                  {priceUSD && typeof priceUSD === 'number' && !isNaN(priceUSD) && isFinite(priceUSD) ? (priceUSD / 3000).toFixed(8) : '0.00000000'} ETH
                </PriceEthAmount>
              </PriceDisplay>
              <DividerLine />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {priceChange24h !== null && (
                  <StatRow>
                    <StatRowLabel>24h Change</StatRowLabel>
                    <StatRowValue $highlight style={{ color: priceChange24h !== null && priceChange24h >= 0 ? '#10b981' : '#ef4444' }}>
                      {priceChange24h !== null && typeof priceChange24h === 'number' && !isNaN(priceChange24h) && isFinite(priceChange24h) ? `${priceChange24h >= 0 ? '+' : ''}${priceChange24h.toFixed(2)}%` : 'N/A'}
                    </StatRowValue>
                  </StatRow>
                )}
                {high24h !== null && typeof high24h === 'number' && !isNaN(high24h) && isFinite(high24h) && (
                  <StatRow>
                    <StatRowLabel>24h High</StatRowLabel>
                    <StatRowValue $highlight>${high24h.toFixed(6)}</StatRowValue>
                  </StatRow>
                )}
                {low24h !== null && typeof low24h === 'number' && !isNaN(low24h) && isFinite(low24h) && (
                  <StatRow>
                    <StatRowLabel>24h Low</StatRowLabel>
                    <StatRowValue $highlight>${low24h.toFixed(6)}</StatRowValue>
                  </StatRow>
                )}
                <StatRow>
                  <StatRowLabel>24h Volume</StatRowLabel>
                  <StatRowValue $highlight>
                    ${volume24h && typeof volume24h === 'number' && !isNaN(volume24h) && isFinite(volume24h) ? (volume24h >= 1000000 ? (volume24h / 1000000).toFixed(2) + 'M' : volume24h >= 1000 ? (volume24h / 1000).toFixed(2) + 'K' : volume24h.toFixed(2)) : '0'}
                  </StatRowValue>
                </StatRow>
              </div>
              <DividerLine />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <StatRow>
                  <StatRowLabel>Market Cap</StatRowLabel>
                  <StatRowValue $highlight>{priceUSD ? formatMarketCap(priceUSD) : 'Loading...'}</StatRowValue>
                </StatRow>
                <StatRow>
                  <StatRowLabel>FDV</StatRowLabel>
                  <StatRowValue $highlight>{priceUSD ? formatMarketCap(priceUSD) : 'Loading...'}</StatRowValue>
                </StatRow>
                <StatRow>
                  <StatRowLabel>Liquidity</StatRowLabel>
                  <StatRowValue $highlight>
                    {blockscoutLiquidity !== null 
                      ? blockscoutLiquidity >= 1000 
                        ? `$${(blockscoutLiquidity / 1000).toFixed(2)}K`
                        : `$${blockscoutLiquidity.toFixed(2)}`
                      : poolData?.reserve_in_usd && typeof poolData.reserve_in_usd === 'number' && !isNaN(poolData.reserve_in_usd) && isFinite(poolData.reserve_in_usd) 
                        ? `$${(poolData.reserve_in_usd / 1000).toFixed(2)}K` 
                        : 'N/A'}
                  </StatRowValue>
                </StatRow>
              </div>
            </CardContent>
          </TerminalCard>

          <TerminalCard>
            <CardHeader>
              <CardTitle>Token Info</CardTitle>
            </CardHeader>
            <CardContent>
              <ContractDisplay>
                <PriceLabel>Contract</PriceLabel>
                <ContractAddress>
                  <ContractText>{token.contract_address}</ContractText>
                  <IconButton onClick={() => navigator.clipboard.writeText(token.contract_address)}>
                    <Copy size={12} />
                  </IconButton>
                  <IconButton as="a" href={`${explorerLinkPrefix}/address/${token.contract_address}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={12} />
                  </IconButton>
                </ContractAddress>
              </ContractDisplay>
              <DividerLine />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <StatRow>
                  <StatRowLabel>Network</StatRowLabel>
                  <StatRowValue>Ink</StatRowValue>
                </StatRow>
                <StatRow>
                  <StatRowLabel>Type</StatRowLabel>
                  <StatRowValue>ERC-20</StatRowValue>
                </StatRow>
                {token.created_at && (
                  <StatRow>
                    <StatRowLabel>Created</StatRowLabel>
                    <StatRowValue>
                      {new Date(token.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </StatRowValue>
                  </StatRow>
                )}
              </div>
            </CardContent>
          </TerminalCard>
        </LeftSidebar>

        {/* Chart */}
        <CenterSection>
          <ChartSection style={{ padding: 0, border: 'none', background: 'transparent' }}>
            <TradingPanel
              poolAddress={poolAddress}
              tokenAddress={token.contract_address}
              tokenSymbol={token.symbol}
              ethPriceUSD={ethPriceUSD}
              refreshInterval={30000}
            />
          </ChartSection>
        </CenterSection>

        {/* Right Sidebar */}
        <RightSidebar>
          {token.description && (
            <AboutCard>
              <CardHeader>
                <CardTitle>About</CardTitle>
              </CardHeader>
              <CardContent>
                <AboutText>{token.description}</AboutText>
              </CardContent>
            </AboutCard>
          )}

          {/* Comments Section */}
          <TerminalCard style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardHeader>
              <CardTitle>Comments</CardTitle>
            </CardHeader>
            <CardContent style={{ flex: 1, overflow: 'auto' }}>
              <TokenComments tokenAddress={token.contract_address} />
            </CardContent>
          </TerminalCard>
        </RightSidebar>
      </MainContent>

      {/* Swap Modal */}
      {swapModalOpen && (
        <SwapModalOverlay onClick={() => setSwapModalOpen(false)}>
          <SwapModalContent onClick={(e) => e.stopPropagation()}>
            <SwapModalHeader>
              <SwapModalTitle>Trade {token?.symbol}</SwapModalTitle>
              <SwapModalClose onClick={() => setSwapModalOpen(false)}>
                <X size={20} />
              </SwapModalClose>
            </SwapModalHeader>
            <CustomSwapWidget
              preselectedToToken={token ? {
                address: token.contract_address,
                symbol: token.symbol,
                name: token.name,
                chainId: 57073,
                logo: token.logo_url
              } : undefined}
              embedded={true}
            />
          </SwapModalContent>
        </SwapModalOverlay>
      )}
    </PWAContainer>
  );
};

export default TokenDetailsPagePWA;
